import type { WSCertificateSummary, WSEnvelope } from './types.js';

export function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function encodeWSEnvelope(input: {
  type: string;
  sessionId?: string | null;
  payload?: Record<string, unknown>;
}): string {
  const envelope: WSEnvelope = {
    id: newMessageId(),
    type: input.type,
    session_id: input.sessionId ?? null,
    timestamp: Math.floor(Date.now() / 1000),
    payload: input.payload ?? {},
  };
  return JSON.stringify(envelope);
}

export function decodeWSEnvelope(raw: unknown): WSEnvelope {
  const text =
    typeof raw === 'string'
      ? raw
      : raw instanceof ArrayBuffer
        ? new TextDecoder().decode(raw)
        : String(raw);
  const parsed = JSON.parse(text) as WSEnvelope;
  if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') {
    throw new Error('invalid WebSocket envelope');
  }
  return {
    id: typeof parsed.id === 'string' ? parsed.id : newMessageId(),
    type: parsed.type,
    session_id: parsed.session_id ?? null,
    timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : 0,
    payload: (parsed.payload && typeof parsed.payload === 'object'
      ? parsed.payload
      : {}) as Record<string, unknown>,
  };
}

export function parseCertificateSummaries(payload: Record<string, unknown>): WSCertificateSummary[] {
  const list = payload.certificates;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: WSCertificateSummary[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const certificateId = String(row.certificate_id ?? '');
    if (!certificateId) {
      continue;
    }
    const summary: WSCertificateSummary = {
      certificateId,
      subject: String(row.subject ?? ''),
      issuer: String(row.issuer ?? ''),
      serialNumber: String(row.serial_number ?? ''),
      validFrom: Number(row.valid_from ?? 0),
      validTo: Number(row.valid_to ?? 0),
      certificateType: String(row.certificate_type ?? 'signature'),
    };
    if (row.key_usage != null) {
      summary.keyUsage = String(row.key_usage);
    }
    out.push(summary);
  }
  return out;
}

export function toWsLoginUrl(agentBaseUrl: string): string {
  const url = new URL(agentBaseUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/ws/v1/login';
  url.search = '';
  url.hash = '';
  return url.toString();
}
